/**
 * Dev Notifications Page
 *
 * Manual interface for creating and testing notifications during development.
 * Allows developers to:
 * - Create test notifications with custom data
 * - Use pre-filled templates for common notification types
 * - View recent notifications
 * - Delete test notifications for cleanup
 *
 * Available at /dev/notifications in development mode only.
 *
 * @module pages/DevNotificationsPage
 */

import { useState } from 'react';
import { DevNotificationForm } from '@/components/DevNotificationForm';
import { useNotificationsGraphQL } from '@/graphql/hooks/useNotificationsGraphQL';
import { useCreateNotificationGraphQL } from '@/hooks/graphql/useNotificationMutationsGraphQL';
import { Heading } from '@/components/atoms/Heading';
import { Text } from '@/components/atoms/Text';
import logger from '@/utils/logger';
import type { Notification } from '@/types/notification';
import type { CreateNotificationInput } from '@/hooks/graphql/useNotificationMutationsGraphQL';

export function DevNotificationsPage() {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch recent notifications for display via GraphQL
  const { data: notificationsData, isLoading } = useNotificationsGraphQL({
    limit: 20,
  });

  // Create notification mutation via GraphQL
  const { createNotification, loading: isCreating } = useCreateNotificationGraphQL();

  const handleCreateNotification = async (request: CreateNotificationInput) => {
    try {
      logger.info('DevNotifications', 'Creating notification', request);
      // Convert null actorId to undefined for GraphQL compatibility
      const graphqlInput = {
        ...request,
        actorId: request.actorId ?? undefined,
      };
      const notification = await createNotification(graphqlInput);

      if (notification) {
        logger.info('DevNotifications', 'Notification created successfully', {
          globalId: notification.globalId,
          type: notification.notificationType,
        });
        setSuccessMessage(`Notification created: ${notification.globalId}`);
        setErrorMessage(null);

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error: any) {
      logger.error('DevNotifications', 'Failed to create notification', error);
      setErrorMessage(error.message || 'Failed to create notification');
      setSuccessMessage(null);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border bg-surface-1">
        <Heading level="h2">Notification Testing</Heading>
        <Text className="text-muted mt-1">
          Create and test notifications manually. This page is only available in development mode.
        </Text>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-6 space-y-8">
          {/* Status Messages */}
          {successMessage && (
            <div className="p-4 bg-success/10 border border-success/30 rounded-lg">
              <Text className="text-success font-medium">{successMessage}</Text>
            </div>
          )}

          {errorMessage && (
            <div className="p-4 bg-danger/10 border border-danger/30 rounded-lg">
              <Text className="text-danger font-medium">{errorMessage}</Text>
            </div>
          )}

          {/* Notification Creation Form */}
          <div>
            <DevNotificationForm
              onSubmit={handleCreateNotification}
              isSubmitting={isCreating}
            />
          </div>

          {/* Recent Notifications List */}
          <div>
            <Heading level="h3" className="mb-4">
              Recent Notifications
            </Heading>

            {isLoading ? (
              <Text className="text-muted">Loading notifications...</Text>
            ) : notificationsData && notificationsData.notifications.length > 0 ? (
              <div className="space-y-3">
                {notificationsData.notifications.map((notification) => (
                  <NotificationCard key={notification.globalId} notification={notification} />
                ))}
              </div>
            ) : (
              <Text className="text-muted italic">
                No notifications yet. Create one using the form above.
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Notification Card Component
 *
 * Displays a single notification in the recent list
 */
interface NotificationCardProps {
  notification: Notification;
}

function NotificationCard({ notification }: NotificationCardProps) {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-mono text-muted">
              {notification.globalId}
            </span>
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded ${
                notification.urgency === 'high'
                  ? 'bg-danger/20 text-danger'
                  : notification.urgency === 'medium'
                  ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400'
                  : 'bg-primary/20 text-primary'
              }`}
            >
              {notification.urgency}
            </span>
            <span className="text-xs text-muted">{notification.notificationType}</span>
          </div>

          {/* Title and Body */}
          <h3 className="font-semibold text-foreground mb-1">{notification.title}</h3>
          {notification.body && (
            <Text className="text-sm mb-2">{notification.body}</Text>
          )}

          {/* Metadata */}
          <div className="text-xs text-muted space-y-0.5">
            <div>
              <span className="font-medium">Recipient:</span> {notification.recipientId}
            </div>
            {notification.actorId && (
              <div>
                <span className="font-medium">Actor:</span> {notification.actorId}
              </div>
            )}
            {notification.actionUrl && (
              <div>
                <span className="font-medium">Action URL:</span>{' '}
                <a
                  href={notification.actionUrl}
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {notification.actionUrl}
                </a>
              </div>
            )}
            <div>
              <span className="font-medium">Created:</span>{' '}
              {new Date(notification.createdAt).toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Status:</span> {notification.status}
              {notification.readAt && ` (read at ${new Date(notification.readAt).toLocaleString()})`}
            </div>
          </div>

          {/* Payload */}
          {notification.payload && Object.keys(notification.payload).length > 0 && (
            <details className="mt-3">
              <summary className="text-xs font-medium text-foreground cursor-pointer hover:text-primary">
                View Payload
              </summary>
              <pre className="mt-2 text-xs bg-surface-2 border border-border p-2 rounded overflow-x-auto">
                {JSON.stringify(notification.payload, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
